import React, { ReactElement, useCallback, useRef } from 'react'

import { useHandler } from '@surglogs-app/hooks'
import { PromptFunctionProps, usePrompt } from '@surglogs-app/prompt'

import {
  useBeforeLeaveGuard,
  OnBeforeLeavePromptFunction,
} from './useBeforeLeaveGuard'

type OnCloseHandler = (
  e: KeyboardEvent | MouseEvent | React.MouseEvent | React.KeyboardEvent,
) => unknown | Promise<unknown>

export type BeforeLeavePromptProps = PromptFunctionProps<boolean>

/**
 * Renders a leave prompt that can be used to prevent leaving the screen
 *
 * Returns onClose handler that can be used in Visage Dialog components to prevent closing or to manually call it on mouse event, etc
 */
export function useBeforeLeavePrompt(
  /**
   * Prompt renderer, renders a prompt
   */
  promptRenderer: (props: BeforeLeavePromptProps) => ReactElement,
  /**
   * Prompt condition, determines whether we should show prompt or not
   */
  promptCondition: () => boolean,

  opts?: Partial<{
    /**
     * OnClose handler is called when prompt is confirmed
     */
    onClose: null | (() => void)
    /**
     * Whether to ignore query changes or not
     */
    ignoreSilentChanges: boolean
  }>,
): OnCloseHandler {
  const [prompt, opened] = usePrompt()
  const isBlocked = promptCondition() && !opened
  const skipBlockRef = useRef(false)

  const waitForPrompt = useHandler(async () => {
    if (isBlocked) {
      return prompt(promptRenderer) as Promise<boolean>
    }

    return true
  })

  const callback = useHandler<OnBeforeLeavePromptFunction>(
    async ({ reject, allow, silent }) => {
      const res =
        (opts?.ignoreSilentChanges ?? true) === true && silent
          ? await Promise.resolve(true)
          : await waitForPrompt()

      if (res) {
        allow()
      } else {
        reject()
      }
    },
  )

  useBeforeLeaveGuard(callback, isBlocked, skipBlockRef)

  const onClose = opts?.onClose
  return useCallback(
    async (e) => {
      const res = await waitForPrompt()
      if (!res) {
        if (!e) {
          return
        }
        e.preventDefault()
      } else {
        skipBlockRef.current = true
        onClose?.()
      }
    },
    [waitForPrompt, onClose],
  )
}
